// Ready narration goes straight from the browser into the bucket (presigned PUT, ≤ 200 MB):
// the API only signs the request, the worker re-checks the file with ffprobe.
import {
  DURATION_WARN,
  MAX_UPLOAD_BYTES,
  STT_USD_PER_HOUR,
  UPLOAD_EXTENSIONS,
  type AdminUploadUrlResponse,
  type Lang,
} from '@fonus/shared';
import { api, ApiFailure } from './api.ts';

export const ACCEPT = '.mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a';

export function fileProblem(file: File): string | null {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (!(UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) return 'Нужен файл mp3, wav или m4a';
  if (file.size === 0) return 'Пустой файл';
  if (file.size > MAX_UPLOAD_BYTES) return 'Файл больше 200 МБ';
  return null;
}

/** Duration read by the browser (metadata only); null if it cannot tell. */
export function audioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    const done = (v: number | null) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    a.preload = 'metadata';
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) ? a.duration : null);
    a.onerror = () => done(null);
    a.src = url;
  });
}

export function durationNote(sec: number | null): string | null {
  if (sec === null) return null;
  if (sec < DURATION_WARN.minSec) return 'Короче 3 минут — для сказки на ночь коротковато';
  if (sec > DURATION_WARN.maxSec) return 'Длиннее 15 минут — ребёнок может не дослушать';
  return null;
}

export const sttUsd = (sec: number) => (sec / 3600) * STT_USD_PER_HOUR;

function put(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (share: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new ApiFailure(
            xhr.status,
            `Хранилище ответило ${String(xhr.status)}`,
            'NETWORK',
            {},
            null,
          ),
        );
    };
    xhr.onerror = () =>
      reject(new ApiFailure(0, 'Не удалось отправить файл в хранилище', 'NETWORK', {}, null));
    xhr.send(file);
  });
}

/** Signs and uploads one file; returns the bucket key for the story form. */
export async function uploadNarration(
  lang: Lang,
  file: File,
  onProgress: (share: number) => void,
): Promise<string> {
  const signed = await api<AdminUploadUrlResponse>('/admin/uploads', {
    method: 'POST',
    body: { lang, fileName: file.name, contentType: file.type, sizeBytes: file.size },
  });
  await put(signed.url, signed.headers, file, onProgress);
  onProgress(1);
  return signed.key;
}
